import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  UseToastOptions,
  chakra,
  useMediaQuery,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react'
import MainLayout from '@/components/MainLayout'
import { isAllStateParams, UserState, useSignUpUser } from '@/API/userAPI'
import { checkEmailValidation, ToastUtil } from '@/util/util'
import { useDaumPostcodePopup } from 'react-daum-postcode'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import UserInfoConsent from '@/components/user/UserInfoConsent'
import { HiEye } from 'react-icons/hi2'
import { HiEyeOff } from 'react-icons/hi'
import PhoneInput from '@/components/form/PhoneInput'
import { useTranslation } from 'react-i18next'

const SignUp = () => {
  const [isMobile] = useMediaQuery('(max-width: 768px)')
  const toast = useToast()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const open = useDaumPostcodePopup(
    'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js',
  )
  const { mutate: signUpUser, isSuccess: isSignUpUserSuccess } = useSignUpUser()
  const [address, setAddress] = useState<string>('')
  const [addressDetail, setAddressDetail] = useState<string>('')
  const [postCode, setPostCode] = useState<string>('')
  const [isConsent, setIsConsent] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState<boolean>(false)

  const handleComplete = (data: any) => {
    let extraAddress = ''

    setAddress(data.roadAddress)
    if (data.addressType === 'R') {
      if (data.bname !== '') {
        extraAddress += data.bname
      }
      if (data.buildingName !== '') {
        extraAddress +=
          extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName
      }
      setAddressDetail(extraAddress !== '' ? ` (${extraAddress})` : '')
    }
    setPostCode(data.zonecode)
  }

  const handleClick = () => {
    open({ onComplete: handleComplete })
  }

  const toggleSignUpUser = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const target = e.target as HTMLFormElement
    const inputs = Array.from(target.elements) as Array<HTMLInputElement>
    const params: Partial<UserState> = {}

    inputs.map((input) => {
      if (input.tagName === 'INPUT' && !input.id.includes('field')) {
        const id = input.id as keyof UserState
        params[id] = input.value
      }
    })

    // TODO: 비밀번호 show / setShow
    // TODO2: 휴대폰 번호 '-' string 자동 replace

    // 모든 프로퍼티 존재 여부 체크
    if (isAllStateParams(params)) {
      const isVaildEmail = checkEmailValidation(params.email)
      const isCorrectPassword =
        params.password && params.password === params.confirm_password
      const isValidAddress = !!params.address && !!postCode

      if (isVaildEmail && isCorrectPassword && isValidAddress && isConsent) {
        const validatePhone = params.phone.replace(/-/gi, '')
        signUpUser({
          ...params,
          phone: validatePhone,
        } as UserState)
      } else if (!isVaildEmail) {
        toast(
          ToastUtil(
            t('user.signup.emailError'),
            'warning',
            t('user.signup.emailErrorDesc'),
          ) as UseToastOptions,
        )
      } else if (!isCorrectPassword) {
        toast(
          ToastUtil(
            t('user.signup.passwordError'),
            'warning',
            t('user.signup.passwordErrorDesc'),
          ) as UseToastOptions,
        )
      } else if (!isValidAddress) {
        toast(
          ToastUtil(
            t('user.signup.addressError'),
            'warning',
            t('user.signup.addressErrorDesc'),
          ) as UseToastOptions,
        )
      } else if (!isConsent) {
        toast(
          ToastUtil(
            t('user.signup.consentError'),
            'warning',
            t('user.signup.consentErrorDesc'),
          ) as UseToastOptions,
        )
      }
    }
  }

  useEffect(() => {
    if (isSignUpUserSuccess) {
      toast(
        ToastUtil(
          t('user.signup.signupSuccess'),
          'success',
          t('user.signup.signupSuccessDesc'),
        ) as UseToastOptions,
      )
      navigate('/user/login')
    }
  }, [isSignUpUserSuccess])

  return (
    <MainLayout>
      <Flex
        minH="700px"
        alignItems="center"
        flexDirection="column"
        py={isMobile ? 8 : 16}
        px={isMobile ? 6 : '10%'}
      >
        <Flex
          my={isMobile ? '60px' : '80px'}
          alignItems="center"
          justifyContent="center"
        >
          <Heading color="#004A8D" fontSize={isMobile ? '36px' : '50px'}>
            {t('user.signup.title')}
          </Heading>
        </Flex>
        <chakra.form onSubmit={toggleSignUpUser}>
          <FormControl
            display="flex"
            flexDirection="column"
            flexWrap="wrap"
            gap={isMobile ? 4 : 8}
            justifyContent="center"
            alignItems="center"
            w={isMobile ? 'full' : '706px'}
          >
            <Flex
              flex={1}
              w="full"
              flexDirection="column"
              gap={2}
              id={`input-group`}
            >
              <FormLabel fontSize={isMobile ? '16px' : '18px'} fontWeight={500}>
                {t('user.signup.emailLabel')}
              </FormLabel>
              <Input
                id="email"
                h={isMobile ? '50px' : '60px'}
                bgColor="#F7F7F7"
                borderRadius={0}
                border="none"
              />
            </Flex>
            <Flex
              flex={1}
              w="full"
              flexDirection="column"
              gap={2}
              id={`input-group`}
            >
              <FormLabel fontSize={isMobile ? '16px' : '18px'} fontWeight={500}>
                {t('user.signup.passwordLabel')}
              </FormLabel>
              <InputGroup>
                <Input
                  id="password"
                  h={isMobile ? '50px' : '60px'}
                  bgColor="#F7F7F7"
                  borderRadius={0}
                  border="none"
                  type={showPassword ? 'text' : 'password'}
                />
                <InputRightElement height="100%">
                  <IconButton
                    size="lg"
                    bg="transparent !important"
                    variant="ghost"
                    aria-label={
                      showPassword ? 'Mask password' : 'Reveal password'
                    }
                    icon={showPassword ? <HiEyeOff /> : <HiEye />}
                    boxSize="7"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </InputRightElement>
              </InputGroup>
            </Flex>
            <Flex
              flex={1}
              w="full"
              flexDirection="column"
              gap={2}
              id={`input-group`}
            >
              <FormLabel fontSize={isMobile ? '16px' : '18px'} fontWeight={500}>
                {t('user.signup.passwordConfirmLabel')}
              </FormLabel>
              <InputGroup>
                <Input
                  id="confirm_password"
                  h={isMobile ? '50px' : '60px'}
                  bgColor="#F7F7F7"
                  borderRadius={0}
                  border="none"
                  type={showPasswordConfirm ? 'text' : 'password'}
                />
                <InputRightElement height="100%">
                  <IconButton
                    size="lg"
                    bg="transparent !important"
                    variant="ghost"
                    aria-label={
                      showPasswordConfirm ? 'Mask password' : 'Reveal password'
                    }
                    icon={showPasswordConfirm ? <HiEyeOff /> : <HiEye />}
                    boxSize="7"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  />
                </InputRightElement>
              </InputGroup>
            </Flex>
            <Flex
              flex={1}
              w="full"
              flexDirection="column"
              gap={2}
              id={`input-group`}
            >
              <FormLabel fontSize={isMobile ? '16px' : '18px'} fontWeight={500}>
                {t('user.signup.nameLabel')}
              </FormLabel>
              <Input
                id="fullname"
                h={isMobile ? '50px' : '60px'}
                bgColor="#F7F7F7"
                borderRadius={0}
                border="none"
              />
            </Flex>
            <Flex
              flex={1}
              w="full"
              flexDirection="column"
              gap={2}
              id={`input-group`}
            >
              <FormLabel fontSize={isMobile ? '16px' : '18px'} fontWeight={500}>
                {t('user.signup.phoneLabel')}
              </FormLabel>
              <PhoneInput isMobile={isMobile} />
            </Flex>
            <Flex
              flex={1}
              w="full"
              flexDirection="column"
              gap={4}
              id={`input-group`}
            >
              <FormLabel fontSize={isMobile ? '16px' : '18px'} fontWeight={500}>
                {t('user.signup.addressLabel')}
              </FormLabel>
              <Flex gap={4}>
                <Input
                  h={isMobile ? '50px' : '60px'}
                  bgColor="#F7F7F7"
                  borderRadius={0}
                  border="none"
                  value={postCode}
                  onChange={(e) => setAddress(e.target.value)}
                  isDisabled
                />
                <Button
                  w="223px"
                  h={isMobile ? '50px' : '60px'}
                  border="1.5px solid #787E88"
                  borderRadius="0"
                  color="#787E88"
                  onClick={handleClick}
                >
                  {t('user.signup.postCodeButton')}
                </Button>
              </Flex>
              <Input
                id="address"
                h={isMobile ? '50px' : '60px'}
                bgColor="#F7F7F7"
                borderRadius={0}
                border="none"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                isDisabled
              />
              <Input
                id="address_detail"
                h={isMobile ? '50px' : '60px'}
                bgColor="#F7F7F7"
                borderRadius={0}
                border="none"
                value={addressDetail}
                onChange={(e) => setAddressDetail(e.target.value)}
              />
            </Flex>
            <Flex
              flex={1}
              w="full"
              flexDirection="column"
              gap={2}
              id={`input-group`}
            >
              <FormLabel fontSize={isMobile ? '16px' : '18px'} fontWeight={500}>
                {t('user.signup.companyLabel')}
              </FormLabel>
              <Input
                id="company_name"
                h={isMobile ? '50px' : '60px'}
                bgColor="#F7F7F7"
                borderRadius={0}
                border="none"
              />
            </Flex>
            <UserInfoConsent
              isConsent={isConsent}
              setIsConsent={setIsConsent}
            />
            <Flex w="full" justifyContent="center" my={4}>
              <Button
                type="submit"
                w="full"
                h={isMobile ? '50px' : '60px'}
                bgColor="#0D3A65"
                borderRadius={0}
              >
              {t('user.signup.signupButton')}
            </Button>
            </Flex>
          </FormControl>
        </chakra.form>
      </Flex>
    </MainLayout>
  )
}

export default SignUp
